import lucene from 'lucene'
import { IDataSchema } from '../core'
import {
  LuceneDateOperations,
  IFilterOption,
  LuceneNodeWithImplicitChildren,
  LuceneParsedQuery,
  LogicalOperator,
} from '../types'
import { DATE_OPERATION_PATTERN } from './date-operations'
import { parseNode } from './lucene-comparator'
import { getFieldDefinition, isValidComparator } from './simple-query-helpers'

// Can only handle when all of the operators are the same and they are all '<implicit>' fields
// - if not, we just ignore this node
const canHandleNodeWithImplicitChildren = (
  node: lucene.BinaryAST,
  operator: string,
): boolean => {
  if (node.operator !== operator || (operator !== 'AND' && operator !== 'OR')) {
    return false
  }

  let leftValid = true
  if ('field' in node.left) {
    leftValid =
      node.left.field === '<implicit>' && ('term' in node || 'operator' in node)
  } else if ('right' in node.left) {
    leftValid = canHandleNodeWithImplicitChildren(node.left, operator)
  }

  let rightValid = true
  if ('field' in node.right) {
    rightValid =
      node.right.field === '<implicit>' &&
      ('term' in node || 'operator' in node)
  } else if ('right' in node.right) {
    rightValid = canHandleNodeWithImplicitChildren(node.right, operator)
  }

  return leftValid && rightValid
}

// An opportunity to ignore nodes we don't currently handle in the "simple" UI
const canHandleNode = (node: lucene.Node): boolean => {
  if ('term_min' in node) {
    if (
      node.term_min !== '*' &&
      node.term_max !== '*' &&
      node.inclusive !== 'both'
    ) {
      // The UI can currently only handle "is between" if it is inclusive
      return false
    }
  }
  return true
}

// This replaces NOT with the abbreviated dash so that switching from Advanced to Query builder works with NOT
// This does not replace the actual string in the UI.  It replaces it at the time of parsing to a Lucene node object
const replaceNotWithDash = (query: string): string => {
  return query.replace(/\bNOT\s+(\([^)]+\)|\S+)/g, (_, field) => {
    return `-${field}`
  })
}

export const isQueryUnsupportedInUi = (
  parsedQuery: LuceneParsedQuery | null,
  dataSchema: IDataSchema | undefined,
  fieldOptions: IFilterOption[],
) => {
  if (!parsedQuery) {
    // Parsed query is null when it can't be parsed
    return true
  }

  // Checks to see if any fields use a comparator that is unsupported by the UI
  let usesAdvancedComparators = false
  parsedQuery.nodes.forEach((node) => {
    const { fieldName, comparator } = parseNode(node)
    const fieldOption = fieldOptions.find(({ value }) => fieldName === value)
    const fieldDefinition = getFieldDefinition(fieldOption, dataSchema)
    if (!isValidComparator(fieldDefinition, comparator)) {
      usesAdvancedComparators = true
    }
  })

  const usesAdvancedFeature = isNodeAdvanced(parsedQuery?.ast, false)
  return usesAdvancedFeature || usesAdvancedComparators
}

/**
 * Recursively used to determine whether an query needs to use the advanced query builder
 * Currently this is when OR is used
 */
export const isNodeAdvanced = (
  node: lucene.AST | lucene.Node | LuceneNodeWithImplicitChildren | null,
  found: boolean,
): boolean => {
  if (!node || found) return found

  // We don't support OR.  We don't take into account parenthesis because this is only needed for OR statements at the moment.
  // @ts-expect-error - lucene types are sketchy
  if (node.operator === 'OR') found = true

  if ('left' in node) found = isNodeAdvanced(node.left, found)
  if ('right' in node) found = isNodeAdvanced(node.right, found)
  return found
}

const getFlatNodes = (
  parsedQuery: lucene.AST | lucene.Node | LuceneNodeWithImplicitChildren,
): Array<lucene.Node | LuceneNodeWithImplicitChildren> => {
  if ('right' in parsedQuery) {
    if ('field' in parsedQuery) {
      // NodeWithImplicitChildren
      if (
        canHandleNodeWithImplicitChildren(parsedQuery, parsedQuery.operator)
      ) {
        return [parsedQuery]
      }
      return []
    }
    // lucene.AST: { left, operator, right }
    return [
      ...getFlatNodes(parsedQuery.left),
      ...getFlatNodes(parsedQuery.right),
    ]
  } else if ('left' in parsedQuery) {
    // lucene.LeftOnlyAST: { left }
    return getFlatNodes(parsedQuery.left)
  } else if ('field' in parsedQuery) {
    // lucene.Node
    if (canHandleNode(parsedQuery)) {
      return [parsedQuery]
    }
    return []
  } else {
    return []
  }
}

/**
 * Returns a lucene query string based on an AST object. When the query contains
 * date op refs they are also replaced with their respective date op values.
 */
export const stringifyQuery = (
  ast: lucene.AST | null,
  dateOperations: LuceneDateOperations | null,
) => {
  if (!ast) {
    return ''
  }

  const query = lucene.toString(ast)
  return dateOperations ? convertRefsToDateOps(query, dateOperations) : query
}

/**
 * Returns true when one or more of the provided terms contains a date operation
 */
export const hasDateOperations = (term: string | string[]) => {
  const terms = Array.isArray(term) ? term : [term]

  return terms.some((item) => !!item.match(DATE_OPERATION_PATTERN)?.length)
}

/**
 * Replaces date op references in a lucene query or term string with their real
 * values contained in the provided date ops object.
 */
export const convertRefsToDateOps = (
  queryOrTerm: string,
  dateOperations: LuceneDateOperations | null,
) => {
  if (!dateOperations) return queryOrTerm

  const { dateOpsByRef } = dateOperations

  return queryOrTerm.replace(/\$\d/g, (match) => {
    return dateOpsByRef[match]
  })
}

/**
 * Replaces date op values in a lucene query or term string with references
 * contained in the provided date ops object. Sometimes this isn't the original
 * ref if there are duplicate values, but it doesn't matter since it will still
 * refer to the same value.
 */
export const convertDateOpsToRefs = (
  queryOrTerm: string,
  dateOperations: LuceneDateOperations | null,
) => {
  if (!dateOperations) return queryOrTerm

  const { dateOpsByRef } = dateOperations

  return queryOrTerm.replace(DATE_OPERATION_PATTERN, (match) => {
    const [ref] =
      Object.entries(dateOpsByRef).find(([, value]) => value === match) || []
    return ref || match
  })
}

/**
 * Processes a lucene query string to extract date operations and store them in
 * a reference object so they can be inserted again later. Returns a date ops
 * object if matches were found, otherwise returns null.
 */
const parseAndRemoveDateOpsFromQuery = (
  query: string,
): LuceneDateOperations | null => {
  let dateOpsCount = 0
  const dateOpsByRef: Record<string, string> = {}

  const cleanQuery = query.replace(DATE_OPERATION_PATTERN, (match) => {
    dateOpsCount++

    const ref = `$${dateOpsCount}`
    dateOpsByRef[ref] = match

    return ref
  })

  return dateOpsCount
    ? { dateOpsByRef, cleanQuery, originalQuery: query }
    : null
}

/**
 * Processes a lucene query string and returns an object containing the AST and
 * other objects.
 *
 * The backend handles date operations on submit, but our frontend lucene parser
 * throws an error when it hits `/` or other characters used in date ops. To get
 * around this we strip date ops out of the query string before parsing. If a
 * date ops object is returned we include this in the processed query passed to
 * other UI components so the unmodified values can be shown where appropriate.
 */
export const parseQuery = (query: string): LuceneParsedQuery | null => {
  let ast = null

  const dateOperations = parseAndRemoveDateOpsFromQuery(query)

  const cleanQuery = replaceNotWithDash(dateOperations?.cleanQuery || query)

  try {
    ast = lucene.parse(cleanQuery)

    return 'left' in ast
      ? // Valid query
        {
          ast,
          dateOperations,
          nodes: getFlatNodes(ast),
        }
      : // Empty query
        { ast: null, dateOperations: null, nodes: [] }
  } catch {
    // Invalid query
    return null
  }
}

export const defaultNodeRangedTermFields = {
  fieldLocation: {
    start: { column: 0, line: 0, offset: 0 },
    end: { column: 0, line: 0, offset: 0 },
  },
}

export const defaultNodeTermFields = {
  boost: null,
  prefix: null,
  quoted: true,
  regex: false,
  similarity: null,
  fieldLocation: {
    start: { column: 0, line: 0, offset: 0 },
    end: { column: 0, line: 0, offset: 0 },
  },
  termLocation: {
    start: { column: 0, line: 0, offset: 0 },
    end: { column: 0, line: 0, offset: 0 },
  },
  term: '',
}

export const appendNode = (
  ast: lucene.AST | null,
  node: lucene.Node | LuceneNodeWithImplicitChildren,
  operator: LogicalOperator = 'AND',
): lucene.AST => {
  if (ast && 'left' in ast) {
    return { left: ast, operator, right: node }
  } else {
    return { left: node as lucene.Node } // First append, only left required
  }
}

// Recursively goes through `ast` and replaces (or removes) the provided `node` - this is
// a pure function so if a node is replaced or removed its parent will be a new object to
// avoid mutating the original AST.
const replaceChildNode = (
  ast: lucene.AST | lucene.Node,
  node: lucene.Node | LuceneNodeWithImplicitChildren,
  replacement: lucene.Node | LuceneNodeWithImplicitChildren | null,
): lucene.AST | lucene.Node | LuceneNodeWithImplicitChildren | null => {
  if ('right' in ast) {
    // Recursively replace both sides of the BinaryAST
    const left = replaceChildNode(ast.left, node, replacement)
    const right = replaceChildNode(ast.right, node, replacement)
    if (left !== ast.left || right !== ast.right) {
      if (left === null && right === null) {
        return null
      }
      // One side was removed
      if (left === null) {
        return right
      }
      if (right === null) {
        return left
      }
      // Make sure we return a new object because left or right has changed.
      // Preserve parenthesized flag so group structure survives node replacement.
      const result: any = { left, operator: ast.operator, right }
      if ((ast as any).parenthesized) result.parenthesized = true
      return result
    }
  } else if ('left' in ast && ast.left === node) {
    return replacement
  } else if (ast === node) {
    return replacement
  }

  // Nothing changed or the node wasn't found
  return ast
}

export const replaceNode = (
  ast: lucene.AST | null,
  node: lucene.Node | LuceneNodeWithImplicitChildren,
  replacement: lucene.Node | LuceneNodeWithImplicitChildren | null,
): lucene.AST | null => {
  if (ast) {
    const replaced = replaceChildNode(ast, node, replacement)
    if (replaced) {
      if (!('left' in replaced)) {
        // `replaced` is a single node - wrap in a LeftOnlyAST
        return { left: replaced }
      }
      // `replaced` is an AST
      return replaced
    }
  }
  return null
}

export const removeNode = (
  ast: lucene.AST | null,
  node: lucene.Node | LuceneNodeWithImplicitChildren,
) => replaceNode(ast, node, null)
